import { useMemo, useState } from "react";
import { FixaModal } from "../../../shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "../../../shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "../../../shared/wrappers/FixaModal/FixaModalHeader";
import { useTranslation } from "@coworker/locales";
import { StoreTeam } from "@coworker/types/lib/storeteam";
import { TeamStatus } from "../../../shared/types/team";
import { TeamModalInputs } from "./TeamModalInputs";
import { NewTeamDataType } from "../../types/TeamMutationTypes";
import TeamModalFooter from "./TeamModalFooter";

interface TeamModalProps {
  visible: boolean;
  close: () => void;
  storeId: string;
  hasActiveProductQualityTeam: boolean;
  submitButtonText?: string;
  onSubmit: (newTeamData: NewTeamDataType) => void;
  isSubmitting: boolean;
  initialData?: StoreTeam | null;
  handleDelete?: () => void;
  isDeleting?: boolean;
}

const TeamModal = ({
  visible,
  close,
  storeId,
  hasActiveProductQualityTeam,
  onSubmit,
  isSubmitting,
  initialData,
  handleDelete,
  isDeleting,
}: TeamModalProps) => {
  const { t } = useTranslation();

  const defaultTeamData = useMemo(
    () => ({
      store_id: storeId,
      status: TeamStatus.ACTIVE,
    }),
    [storeId]
  );

  const [newTeamData, setNewTeamData] = useState<
    Omit<NewTeamDataType, "store_id" | "status">
  >({
    name: initialData?.name || "",
    is_product_quality: initialData?.is_product_quality || false,
    updated_at: Date.now(),
    ...(initialData
      ? { team_id: initialData.id, created_at: initialData.created_at }
      : { created_at: Date.now() }),
  });

  const submitTranslationText = useMemo(() => {
    if (initialData) {
      return t("updateNameString", { name: initialData.name });
    }
    return t("createNameString", { name: newTeamData.name });
  }, [initialData, newTeamData, t]);

  const hasProductQualityTeamError = useMemo(() => {
    return (
      !initialData?.is_product_quality &&
      hasActiveProductQualityTeam &&
      newTeamData.is_product_quality
    );
  }, [hasActiveProductQualityTeam, newTeamData.is_product_quality]);

  const handleChange = (
    field: keyof TeamModalInputs,
    value: TeamModalInputs[typeof field]
  ) => {
    setNewTeamData((prev) => ({
      ...prev,
      [field === "isProductQualityTeam" ? "is_product_quality" : "name"]: value,
    }));
  };

  const handleSubmit = async () => {
    await onSubmit({ ...defaultTeamData, ...newTeamData });
    if (!initialData?.id) {
      setNewTeamData({
        name: "",
        is_product_quality: false,
        updated_at: Date.now(),
        created_at: Date.now(),
      });
    }
  };

  return (
    <FixaModal handleCloseBtn={close} visible={visible}>
      <FixaSheets
        labelledById="teams-management-team-modal-header"
        alignment="right"
        preserveAlignment={false}
        size="small"
        fullSize
        header={
          <FixaModalHeader
            id="teams-management-team-modal-header"
            title={initialData ? t("editTeamString") : t("addTeamString")}
            floating={false}
          />
        }
        footer={
          <TeamModalFooter
            hasProductQualityTeamError={hasProductQualityTeamError}
            handleSubmit={handleSubmit}
            handleDelete={handleDelete}
            disableDelete={isDeleting || isSubmitting}
            disableSubmit={
              isDeleting ||
              isSubmitting ||
              hasProductQualityTeamError ||
              !newTeamData.name.trim()
            }
            submitTranslationText={submitTranslationText}
          />
        }
      >
        <TeamModalInputs
          onChange={handleChange}
          teamName={newTeamData.name}
          isProductQualityTeam={newTeamData.is_product_quality}
        />
      </FixaSheets>
    </FixaModal>
  );
};

export default TeamModal;
